import { ref, computed } from 'vue'
import { auth, db } from '@/firebase'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import { doc, onSnapshot } from 'firebase/firestore'
import { apiPost } from '@/composables/useApi'
import { getPendingReferralCode, clearPendingReferralCode } from '@/composables/useReferral'

const user = ref(null)
const userData = ref(null)
const userDataReady = ref(false)
// Firebase 第一次回报登录状态后为 true，在此之前不知道用户是否已登录
const authResolved = ref(false)
let initialized = false
let unsubscribeUser = null
let initializedUid = null
let initInFlight = null

const userDetails = computed(() => {
  if (!userData.value) {
    return {
      isPremium: false,
      isPro: false,
      remainingUsage: 0,
      monthlyRemainingUsage: 0,
      maxMonthlyUsage: 200,
      purchasedCredits: 0,
      purchasedCreditsTotal: 0,
      canManageSubscription: false,
      premiumType: '',
      premiumExpiry: ''
    }
  }

  const membership = userData.value.membership || {}
  const planType = membership.planType
  const purchasedCredits = userData.value.purchasedCredits?.remaining ?? 0
  const paidCreditsTotal = userData.value.purchasedCredits?.totalPurchased ?? 0
  const purchasedCreditsTotal = Math.max(
    purchasedCredits,
    paidCreditsTotal + (userData.value.purchasedCredits?.rewardTotal ?? 0)
  )
  const remainingUsage =
    userData.value.quota?.remaining ??
    userData.value.monthlyUsage?.remaining ??
    200
  const monthlyRemainingUsage =
    userData.value.monthlyUsage?.remaining ??
    Math.max(0, remainingUsage - purchasedCredits)
  // 邀请奖励也在永久额度里，只有付过钱才算 Pro
  const hasPurchasedCredits = purchasedCredits > 0 && paidCreditsTotal > 0
  const canManageSubscription = Boolean(
    membership.isSubscription ||
    membership.stripeSubscriptionId ||
    String(membership.plan || '').includes('autoRenewal')
  )
  return {
    isPremium: planType === 'premium',
    isPro: planType === 'pro' || (planType !== 'premium' && hasPurchasedCredits),
    remainingUsage,
    monthlyRemainingUsage,
    maxMonthlyUsage: userData.value.quota?.monthlyLimit ?? (planType === 'premium' ? 10000 : planType === 'pro' ? 5000 : 200),
    purchasedCredits,
    purchasedCreditsTotal,
    hasPurchasedCredits,
    canManageSubscription,
    premiumType: membership.plan,
    premiumExpiry: membership.endDate
  }
})

export const ensureBackendUser = async (currentUser = auth.currentUser) => {
  if (!currentUser) return null
  if (initializedUid === currentUser.uid && userData.value) return userData.value
  if (initInFlight?.uid === currentUser.uid) return initInFlight.promise

  const promise = (async () => {
    try {
      const referralCode = getPendingReferralCode()
      const data = await apiPost('/api/init-user', referralCode ? { referralCode } : {})
      if (referralCode) clearPendingReferralCode()
      if (data.user && auth.currentUser?.uid === currentUser.uid) {
        if (!userDataReady.value || initializedUid !== currentUser.uid) {
          userData.value = data.user
        }
        userDataReady.value = true
        initializedUid = currentUser.uid
      }
      return data.user || null
    } catch (error) {
      return null
    } finally {
      if (initInFlight?.uid === currentUser.uid) {
        initInFlight = null
      }
    }
  })()

  initInFlight = {
    uid: currentUser.uid,
    promise
  }

  return promise
}

const initBackendUser = async (currentUser) => {
  try {
    await ensureBackendUser(currentUser)
  } catch {}
}

export function useAuth() {
  if (!initialized) {
    initialized = true
    onAuthStateChanged(auth, async (newUser) => {
      authResolved.value = true
      if (unsubscribeUser) {
        unsubscribeUser()
        unsubscribeUser = null
      }

      if (newUser) {
        const isDifferentUser = user.value?.uid !== newUser.uid
        user.value = newUser
        if (isDifferentUser) {
          userData.value = null
          userDataReady.value = false
          initializedUid = null
        }

        unsubscribeUser = onSnapshot(doc(db, 'users', newUser.uid), (snapshot) => {
          if (snapshot.exists() && auth.currentUser?.uid === newUser.uid) {
            userData.value = snapshot.data()
            userDataReady.value = true
            initializedUid = newUser.uid
          }
        })

        // Firestore can provide the current plan immediately while backend
        // normalization continues independently.
        void initBackendUser(newUser)
      } else {
        user.value = null
        userData.value = null
        userDataReady.value = false
        initializedUid = null
        initInFlight = null
      }
    })
  }

  const logout = async () => {
    try {
      await signOut(auth)
    } catch {}
  }

  return {
    user,
    userData,
    authResolved,
    userDetails,
    userDataReady,
    logout
  }
}
